import { parseISO, eachDayOfInterval, startOfMonth, endOfMonth, format } from "date-fns";
import { useState, useEffect } from "react";
import {
    VictoryChart,
    VictoryAxis,
    VictoryArea,
    VictoryTooltip,
    VictoryVoronoiContainer,
} from "victory";

export default function SickLeaveGraph() {
    const [dailySick, setDailySick] = useState([]);
    const [staffData, setStaffData] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [selectedDepartment, setSelectedDepartment] = useState("All Departments");
    const [shiftsData, setShiftsData] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            const staffResponse = await fetch("https://api.insightos.co.uk/api/staff", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: 'include',
            });

            const responseShifts = await fetch("https://api.insightos.co.uk/api/monthshifts", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: 'include',
                body: JSON.stringify({ selectedMonth }),
            });

            const shifts = await responseShifts.json();
            setShiftsData(shifts.data);

            const staff = await staffResponse.json();
            const staffMembers = staff.data;
            setStaffData(staffMembers);

            const uniqueDepartments = ["All Departments", ...new Set(staffMembers.map((s) => s.Department))];
            setDepartments(uniqueDepartments);

            const sickData = calculateDailySick(shifts.data, staffMembers, selectedMonth, selectedDepartment);
            setDailySick(sickData);
        };

        fetchData();
    }, [selectedMonth, selectedDepartment]);

    const calculateDailySick = (shiftsData, staffData, month, department) => {
        const startOfCurrentMonth = startOfMonth(month);
        const endOfCurrentMonth = endOfMonth(startOfCurrentMonth);
        const daysInMonth = eachDayOfInterval({ start: startOfCurrentMonth, end: endOfCurrentMonth });

        // Filter staff based on the selected department
        const filteredStaff = department === "All Departments"
            ? staffData
            : staffData.filter((s) => s.Department === department);

        const totalStaff = filteredStaff.length;

        return daysInMonth.map((day) => {
            const dayString = format(day, "yyyy-MM-dd");

            // Get shifts for the current day
            const staffOnShift = shiftsData.filter((shift) => {
                const shiftDate = format(parseISO(shift.ShiftDate), "yyyy-MM-dd");
                return shiftDate === dayString;
            });

            // Count how many of the staff on shift are sick (Sick === 1)
            const sickStaff = staffOnShift.filter((shift) => shift.Sick === 1).length;

            return {
                date: format(day, "dd MMM"),
                sickStaff,
                totalStaff,
            };
        });
    };

    const totalStaff = dailySick.length > 0 ? dailySick[0].totalStaff : 0;

    return (
        <div className="p-6 bg-gray-900 rounded-lg shadow-lg w-full text-white">
            <h1 className="text-3xl font-bold mb-4 text-center text-cyan-400">Staff Sick Leave</h1>

            {/* Filters */}
            <div className="flex justify-center space-x-6 mb-6">
                <select
                    onChange={(e) => setSelectedMonth(new Date(new Date().getFullYear(), parseInt(e.target.value), 1))}
                    value={selectedMonth.getMonth()}
                    className="bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white"
                >
                    {Array.from({ length: 12 }, (_, index) => (
                        <option key={index} value={index}>
                            {format(new Date(2025, index), "MMMM")}
                        </option>
                    ))}
                </select>

                <select
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    value={selectedDepartment}
                    className="bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white"
                >
                    {departments.map((department, index) => (
                        <option key={index} value={department}>
                            {department}
                        </option>
                    ))}
                </select>
            </div>

            {/* Graph */}
            <div className="flex justify-center">
                <VictoryChart
                    domainPadding={20}
                    domain={{ y: [0, totalStaff] }}
                    containerComponent={<VictoryVoronoiContainer />}
                >
                    {/* X-Axis */}
                    <VictoryAxis
                        tickValues={dailySick.map((d) => d.date)}
                        style={{
                            tickLabels: {
                                fill: "#A0F0FF",
                                fontSize: 8,
                                fontWeight: "bold",
                                angle: -45,
                                textAnchor: "end",
                            },
                            axis: { stroke: "#A0F0FF" },
                            grid: { stroke: "#444", strokeDasharray: "4" },
                        }}
                    />

                    {/* Y-Axis */}
                    <VictoryAxis
                        dependentAxis
                        tickFormat={(t) => Math.floor(t)}
                        style={{
                            tickLabels: { fill: "#A0F0FF", fontSize: 12 },
                            axis: { stroke: "#A0F0FF" },
                            grid: { stroke: "#444", strokeDasharray: "4" },
                        }}
                    />

                    {/* Area Graph */}
                    <VictoryArea
                        data={dailySick}
                        x="date"
                        y="sickStaff"
                        style={{
                            data: {
                                fill: "rgba(255, 99, 132, 0.3)",
                                stroke: "#FF6384",
                                strokeWidth: 2,
                                filter: "drop-shadow(0px 4px 8px rgba(255, 99, 132, 0.6))",
                            },
                        }}
                        labels={({ datum }) => `Sick: ${datum.sickStaff}`}
                        labelComponent={<VictoryTooltip style={{ fill: "#4b01ff" }} />}
                    />
                </VictoryChart>
            </div>
        </div>
    );
}
